/**
 * AuthorProSelector
 * Searchable multi-select for professional users (SP/FA) as resource authors.
 * Selected authors render as removable chips; the typeahead adds more.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { useServiceProviderSearch } from '../hooks/useServiceProviderSearch';

type AuthorProSelectorProps = {
  value: string[];
  onChange: (value: string[]) => void;
  /**
   * id -> display name for already-selected authors (from the loaded resource).
   * Seeds the chips so edit mode shows current authors without a lookup.
   */
  initialNames?: Record<string, string>;
};

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function AuthorProSelector({ value, onChange, initialNames }: AuthorProSelectorProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [nameById, setNameById] = useState<Record<string, string>>(initialNames ?? {});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebouncedValue(search, 300);
  const { data: searchResults = [], isFetching } = useServiceProviderSearch(
    debouncedSearch || undefined,
  );

  // Merge resource-provided names when they arrive (async load). Existing
  // entries win so a manual selection's name is never clobbered.
  useEffect(() => {
    if (initialNames) {
      setNameById((prev) => ({ ...initialNames, ...prev }));
    }
  }, [initialNames]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (id: string, name: string) => {
      setNameById((prev) => ({ ...prev, [id]: name }));
      if (!value.includes(id)) {
        onChange([...value, id]);
      }
      setSearch('');
      inputRef.current?.focus();
    },
    [value, onChange],
  );

  const handleRemove = useCallback(
    (id: string) => {
      onChange(value.filter((v) => v !== id));
    },
    [value, onChange],
  );

  const showDropdown = isOpen && debouncedSearch.length >= 2;
  const availableResults = searchResults.filter((p) => !value.includes(p.id));
  const count = value.length;

  return (
    <div ref={dropdownRef} className="relative">
      <label htmlFor="author-pro-search" className="block text-sm font-medium text-gray-700">
        Authors Pro (Providers &amp; Advisors){count > 0 && ` (${count})`}
      </label>

      <div className="relative mt-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          id="author-pro-search"
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (search.length >= 2) setIsOpen(true);
          }}
          className="block w-full rounded-md border border-gray-300 bg-white pl-9 pr-8 py-2 text-sm shadow-sm focus:border-[#026857] focus:ring-1 focus:ring-[#026857]"
          placeholder="Search professionals..."
        />
      </div>

      {showDropdown && isFetching && availableResults.length === 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg p-3 text-sm text-gray-500 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" />
          Searching...
        </div>
      )}

      {showDropdown && !isFetching && availableResults.length > 0 && (
        <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {availableResults.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handleSelect(p.id, p.name)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 truncate"
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      {showDropdown && !isFetching && availableResults.length === 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg p-3 text-sm text-gray-500">
          {searchResults.length > 0
            ? 'All matching professionals already added'
            : 'No professionals found'}
        </div>
      )}

      {count > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {value.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-sm rounded-md border border-[#026857]/30 bg-[#026857]/5 text-[#026857]"
            >
              {nameById[id] || 'Selected professional'}
              <button
                type="button"
                onClick={() => handleRemove(id)}
                className="p-0.5 hover:bg-[#026857]/10 rounded"
                aria-label={`Remove ${nameById[id] || 'professional'}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
